import { useEffect, useRef, useState } from "react";

// Two-way voice over the session websocket. 16 kHz mono PCM (Int16).
export function useVoice(send, subscribe) {
  const [active, setActive] = useState(false);
  const [micMuted, setMicMuted] = useState(false);
  const [speakerMuted, setSpeakerMuted] = useState(false);
  const [devices, setDevices] = useState({ inputs: [], outputs: [] });
  const [micId, setMicId] = useState("");
  const [speakerId, setSpeakerId] = useState("");
  const ctxIn = useRef(null);
  const ctxOut = useRef(null);
  const outGain = useRef(null);
  const stream = useRef(null);
  const nodes = useRef([]);
  const cursor = useRef(0);
  const micMutedRef = useRef(false);
  const canPickSpeaker = typeof window !== "undefined" && "setSinkId" in (window.AudioContext?.prototype || {});

  const loadDevices = async () => {
    const list = await navigator.mediaDevices?.enumerateDevices?.() || [];
    setDevices({
      inputs: list.filter((d) => d.kind === "audioinput"),
      outputs: list.filter((d) => d.kind === "audiooutput"),
    });
  };

  useEffect(() => subscribe((m) => {
    if (m.type !== "audio" || !ctxOut.current) return;
    const bin = atob(m.data);
    const f32 = new Float32Array(bin.length / 2);
    for (let i = 0; i < f32.length; i += 1) {
      f32[i] = (((bin.charCodeAt(i * 2) | (bin.charCodeAt(i * 2 + 1) << 8)) << 16) >> 16) / 32768;
    }
    const ctx = ctxOut.current;
    const buf = ctx.createBuffer(1, f32.length, 16000);
    buf.getChannelData(0).set(f32);
    const srcNode = ctx.createBufferSource();
    srcNode.buffer = buf;
    srcNode.connect(outGain.current);
    const now = ctx.currentTime;
    if (cursor.current < now) cursor.current = now + 0.05;
    srcNode.start(cursor.current);
    cursor.current += buf.duration;
  }), [subscribe]);

  const stopCapture = () => {
    nodes.current.forEach((n) => { try { n.disconnect(); } catch { /* noop */ } });
    nodes.current = [];
    if (stream.current) stream.current.getTracks().forEach((t) => t.stop());
    stream.current = null;
    if (ctxIn.current) { ctxIn.current.close().catch(() => {}); ctxIn.current = null; }
  };

  const startCapture = async (deviceId) => {
    const AC = window.AudioContext || window.webkitAudioContext;
    ctxIn.current = new AC({ sampleRate: 16000 });
    stream.current = await navigator.mediaDevices.getUserMedia({
      audio: { deviceId: deviceId ? { exact: deviceId } : undefined, echoCancellation: true, noiseSuppression: true, autoGainControl: true },
    });
    stream.current.getAudioTracks().forEach((t) => { t.enabled = !micMutedRef.current; });
    const srcNode = ctxIn.current.createMediaStreamSource(stream.current);
    const proc = ctxIn.current.createScriptProcessor(2048, 1, 1);
    const mute = ctxIn.current.createGain();
    mute.gain.value = 0;
    proc.onaudioprocess = (e) => {
      if (micMutedRef.current) return;
      const f32 = e.inputBuffer.getChannelData(0);
      const bytes = new Uint8Array(f32.length * 2);
      for (let i = 0; i < f32.length; i += 1) {
        const v = Math.max(-1, Math.min(1, f32[i])) * 32767;
        bytes[i * 2] = v & 0xff;
        bytes[i * 2 + 1] = (v >> 8) & 0xff;
      }
      let bin = "";
      for (let i = 0; i < bytes.length; i += 1) bin += String.fromCharCode(bytes[i]);
      send({ type: "audio", data: btoa(bin) });
    };
    srcNode.connect(proc);
    proc.connect(mute);
    mute.connect(ctxIn.current.destination);
    nodes.current = [srcNode, proc, mute];
  };

  const stop = () => {
    send({ type: "audio_stop" });
    stopCapture();
    if (ctxOut.current) { ctxOut.current.close().catch(() => {}); ctxOut.current = null; }
    setActive(false);
  };

  const start = async () => {
    try {
      const AC = window.AudioContext || window.webkitAudioContext;
      ctxOut.current = new AC({ sampleRate: 16000 });
      outGain.current = ctxOut.current.createGain();
      outGain.current.gain.value = speakerMuted ? 0 : 1;
      outGain.current.connect(ctxOut.current.destination);
      if (speakerId && canPickSpeaker) await ctxOut.current.setSinkId(speakerId).catch(() => {});
      cursor.current = ctxOut.current.currentTime;
      await startCapture(micId);
      send({ type: "audio_start" });
      setActive(true);
      loadDevices();
    } catch {
      stop();
      throw new Error("micro");
    }
  };

  const toggleMic = () => {
    const v = !micMuted;
    micMutedRef.current = v;
    setMicMuted(v);
    stream.current?.getAudioTracks().forEach((t) => { t.enabled = !v; });
  };

  const toggleSpeaker = () => {
    const v = !speakerMuted;
    setSpeakerMuted(v);
    if (outGain.current) outGain.current.gain.value = v ? 0 : 1;
  };

  const chooseMic = async (id) => {
    setMicId(id);
    if (!active) return;
    stopCapture();
    await startCapture(id);
  };

  const chooseSpeaker = async (id) => {
    setSpeakerId(id);
    if (ctxOut.current && canPickSpeaker) await ctxOut.current.setSinkId(id).catch(() => {});
  };

  useEffect(() => {
    loadDevices();
    return () => stop();
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  return {
    active, toggle: () => (active ? stop() : start()),
    micMuted, toggleMic, speakerMuted, toggleSpeaker,
    devices, micId, speakerId, chooseMic, chooseSpeaker, canPickSpeaker, loadDevices,
  };
}
